/**
 * Get Heard - Frontend Entry Point
 *
 * Thin orchestrator that initializes theme, navigation, locale selectors,
 * page loaders, event handlers, and global keyboard shortcuts.
 * All page-specific logic lives in ./pages/*.ts modules.
 */
import { api, armSessionReload, SIGNIN_PATH } from "./api.ts";
import { loadAiStatus } from "./aiStatus.ts";
import { loadMailStatus } from "./mailStatus.ts";
import { initTheme } from "./theme.ts";
import { initI18n, t } from "./i18n.ts";
import { toast } from "./utils.ts";
import "./components/claimCard.ts";
import "./components/exampleBar.ts";
import { showClaimDialog } from "./components/claimDialog.ts";
import { navigateTo, onNavigate, pageLoaders, prefetchPages, warmPage } from "./router.ts";
import { APP_SECTIONS, appPageHref, canonicalPage, pathFromLegacyHash } from "../../shared/appPages.ts";
import { initTooltips, setTooltipNavigator } from "./tooltip.ts";
import { initAttentionBadge } from "./attention.ts";
import { initTabCounts } from "./tabCounts.ts";
import { initErrorReporting } from "./errorReport.ts";
import { initA11y, initModalFocus } from "./a11y.ts";

import { SAAS_ABSENT_SURFACES } from "../../shared/editionSurfaces.ts";
import { showLoginScreen, type AuthMeta } from "./pages/login.ts";
import { setAppContext } from "./meta.ts";

// The `declare global { interface Window {…} }` burn-down list that used to live here is
// GONE (round 75): the settings island retired the last three `window.*` globals. New code
// never assigns app state or wiring to `window` - STYLEGUIDE "State: passing, sharing,
// mutating" is the rulebook.

// ── Register page loaders ───────────────────────────────
// ⚠ **Each page is its own CHUNK, and this list is the only place that says so** (2026-08-25). They
// were eighteen static imports until a Lighthouse run put 5s of blocked first paint against one
// 2.6MB bundle - a reader on `/compose` was downloading and parsing the settings page, the shorts
// studio and the release editor before their own page could draw. `router.ts`'s `pageLoaders` doc
// comment carries the rules; the one that bites is that these specifiers must stay LITERAL, because
// a computed path collapses the split back into one chunk without failing anything.
//
// ⚠ **`login.ts` is deliberately NOT here.** It is not a routed page - it is the screen the app
// draws INSTEAD of itself when `/api/meta` says there is no session, so deferring it would put a
// chunk fetch in front of the one surface that has to render when the app is least sure of itself.
pageLoaders.dashboard = async () => (await import("./pages/dashboard.ts")).loadDashboard;
pageLoaders.content = async () => (await import("./pages/content.ts")).loadContent;
pageLoaders.insights = async () => (await import("./pages/insights.ts")).loadInsights;
pageLoaders.posts = async () => (await import("./pages/posts.ts")).loadPosts;
pageLoaders.ads = async () => (await import("./pages/ads.ts")).loadAds;
pageLoaders.discover = async () => (await import("./pages/discover.ts")).loadDiscover;
pageLoaders.outlets = async () => (await import("./pages/blogs.ts")).loadBlogs;
pageLoaders.artists = async () => (await import("./pages/artists.ts")).loadArtists;
pageLoaders.releases = async () => (await import("./pages/releases.ts")).loadReleases;
pageLoaders.campaigns = async () => (await import("./pages/campaigns.ts")).loadCampaigns;
pageLoaders.compose = async () => (await import("./pages/compose.ts")).loadCompose;
pageLoaders.queue = async () => (await import("./pages/queue.ts")).loadQueue;
pageLoaders.approved = async () => (await import("./pages/approved.ts")).loadApproved;
pageLoaders.sent = async () => (await import("./pages/sent.ts")).loadSent;
pageLoaders.accepted = async () => (await import("./pages/accepted.ts")).loadAccepted;
pageLoaders.rejected = async () => (await import("./pages/rejected.ts")).loadRejected;
pageLoaders.settings = async () => (await import("./pages/settings.ts")).loadSettings;
pageLoaders.onboarding = async () => (await import("./pages/onboarding.ts")).loadOnboarding;

// ⚠ **The address already says which chunk we need, so ask for it HERE** (2026-08-25, second pass) -
// before `initI18n`, before `/api/meta`, before the ai and mail status reads. Every one of those is
// an await this fetch does not depend on, and boot was letting the page's own code queue behind
// three serial rounds of `/api`: the dashboard chunk was not requested until 301ms on a run whose
// shared chunk had landed at 188ms. `warmPage`'s doc comment carries the measurement and the rule.
// ⚠ `canonicalPage` for `parseRoute`'s reason - `/blogs` is Outlets - and a name this app does not
// have simply warms nothing, which is exactly what the fallback to the dashboard below wants.
// ⚠ `pathFromLegacyHash` because `adoptLegacyHash()` has not run yet and cannot: it sits below the
// top-level await, so an arrival on `#settings` (Stripe's stored return_url) still reads as `/` here.
warmPage(canonicalPage((pathFromLegacyHash(location.hash) ?? location.pathname).replace(/^\/+/, "").split("/")[0] || "dashboard"));

// ── Init ────────────────────────────────────────────────
// Locale first and synchronously: it localizes the static chrome (nav) from the cache before
// anything renders, and every page loader below reads currentLocale() during its render. The
// async initTheme() then reconciles with the workspace's stored mode (same fetch as the theme).
// ⚠ Error reporting goes FIRST, before anything that could throw. The hooks are on `window`, so
// installing them costs nothing and covers every line after this one - including the rest of boot,
// which is where a broken migration or a bad stored theme would surface (round 179).
initErrorReporting();
// ⚠ **A top-level `await`, and everything below it depends on being after it** (2026-08-25). The
// dictionaries left the bundle, so the app's own strings arrive over HTTP now; every line from here
// down calls `t()` directly or renders something that does. `index.html` started this fetch before
// app.js was even parsed, so on a warm cache this settles in the same tick. Legal because the bundle
// is an ES module now - see the `<script type="module">` note in index.html.
await initI18n();
initTheme();
initTooltips();
// ⚠ The router is INJECTED rather than imported by tooltip.ts, which `GhElement` imports and every
// page island therefore pulls in - a static import there closes a cycle back through the pages. This
// is what lets a tooltip carry a link the app can actually navigate (an href alone changes the
// address and loads nothing, because this router does not listen to navigations it did not cause).
setTooltipNavigator(navigateTo);
// The accessibility module (§R). Two calls, two different lifetimes:
// `initModalFocus()` arms document-level listeners and must be up before any modal can open;
// `initA11y()` is a FETCH that reconciles the pre-paint stamp with the workspace's stored answer, and
// is deliberately not awaited by anything - unlike initTheme(), the cache in index.html has already
// painted the right size on every load after the first, so a late arrival costs one frame rather than
// a flash of the wrong palette.
initModalFocus();
void initA11y();

// The skip link (WCAG 2.4.1). Handled here rather than left to the browser because letting
// `href="#main-content"` through would leave that in the URL, and a reload would then land somewhere
// other than the page the owner was on. ⚠ Still true after the move to paths, for a slightly
// different reason: the hash is no longer the address, but it still travels on a reload and the
// boot's `adoptLegacyHash` would then have to reason about it.
document.querySelector<HTMLAnchorElement>(".skip-link")?.addEventListener("click", e => {
	e.preventDefault();
	const main = document.getElementById("main-content");
	main?.focus();
	main?.scrollIntoView();
});

// Returning from Stripe Checkout (round 61): the redirect lands with the query BEFORE
// the hash (?billing=…#settings). Say honestly what happened - the webhook is what flips
// the plan, so "updates in a moment" rather than pretending it's instant - and clean the
// query so a reload doesn't re-toast.
{
	const billingReturn = new URLSearchParams(location.search).get("billing");
	if (billingReturn) {
		// `?billing=topup` was a third arm until 2026-08-09 (the AI top-up packs - shared/plans.ts).
		// Nothing can produce that value now, and an unrecognized one has always fallen through to
		// simply cleaning the query, which is the right answer for a stale bookmark.
		if (billingReturn === "success") toast(t("billing.returnSuccess"), 8000, "success");
		else if (billingReturn === "cancelled") toast(t("billing.returnCancelled"), 5000, "warning");
		history.replaceState(null, "", location.pathname + location.hash);
	}
}

/**
 * THE PHONE'S NAV DRAWER (owner, 2026-08-10: *"the app doesn't work well on mobile"*).
 *
 * All of the layout is CSS (`public/style.css`, the 900px query); this is only the STATE, and it is
 * deliberately one class on `<body>` rather than a style on the sidebar - every visual effect of it
 * lives inside that media query, so a window widened while the drawer is open needs no cleanup.
 *
 * ⚠ **Closing on a nav click is not optional.** The links route in place without a page load, so
 * without this the drawer would sit over the page it just navigated to, and the app would look frozen
 * on the one device where the nav covers the content.
 */
const setNavOpen = (open: boolean): void => {
	document.body.classList.toggle("nav-open", open);
	document.getElementById("nav-toggle")?.setAttribute("aria-expanded", String(open));
	// Focus follows the drawer, both ways: into the first row on open (it is off-screen otherwise and
	// a keyboard user would tab into nothing), back to the button on close.
	if (open) document.querySelector<HTMLElement>(".nav-links a")?.focus();
	else document.getElementById("nav-toggle")?.focus();
};
document.getElementById("nav-toggle")?.addEventListener("click", () => {
	setNavOpen(!document.body.classList.contains("nav-open"));
});
document.getElementById("nav-scrim")?.addEventListener("click", () => setNavOpen(false));

// Navigation links: every page's own tab
document.querySelectorAll<HTMLElement>("[data-page]").forEach(link => {
	link.addEventListener("click", e => {
		e.preventDefault();
		// ⚠ Before navigateTo, not after: the drawer holds focus, and returning it to the toggle has to
		// happen while the page it belongs to is still the one on screen.
		if (document.body.classList.contains("nav-open")) setNavOpen(false);
		navigateTo(link.dataset.page!);
	});
});

/**
 * Where a JOB opens (§AL round 3): the first tab of its bar that is actually offered - Get started
 * while the guide is open, then Dashboard; Outlets whichever edition this is. Read off the rendered
 * bar rather than `APP_SECTIONS`, because the bar is where the edition fork and the guide reveal have
 * already been applied, and a second copy of either decision here would be free to disagree.
 */
function sectionEntry(section: string): string {
	const bar = document.querySelector(`.section-tabs[data-section="${section}"]`);
	const first = [...(bar?.querySelectorAll<HTMLElement>("li:not(.hidden) a[data-page]") ?? [])][0];
	return first?.dataset.page ?? APP_SECTIONS.find(entry => entry.section === section)?.pages[0] ?? "dashboard";
}

// The seven sidebar rows: each opens its job
document.querySelectorAll<HTMLElement>(".nav-links [data-section]").forEach(link => {
	link.addEventListener("click", e => {
		e.preventDefault();
		if (document.body.classList.contains("nav-open")) setNavOpen(false);
		navigateTo(sectionEntry(link.dataset.section!));
	});
});

/** A row's href is where it really goes, so a middle-click opens the same page a click does. */
function refreshSectionHrefs(): void {
	document.querySelectorAll<HTMLAnchorElement>(".nav-links [data-section]").forEach(link => {
		link.href = appPageHref(sectionEntry(link.dataset.section!));
	});
}

/**
 * The wordmark goes home (owner ask, 2026-08-07). "Home" is **the first page in the sidebar** - Get
 * started while the first-run guide is open, Dashboard once it is hidden.
 *
 * ⚠ **Derived at CLICK time from the nav itself, never hard-coded**, and that is the whole design.
 * Writing `navigateTo("dashboard")` here would be wrong for every new install (where Get started is
 * the first row and the page somebody actually wants), and writing the onboarding check here would
 * be a second copy of a decision `/api/meta` already made and line 257 already applied. Reading the
 * rendered nav means this stays true if a row is ever added, removed or reordered, and it is also
 * the literal reading of what was asked for: the first item in the list.
 *
 * ⚠ **`offsetParent` is the visibility test**, not `classList.contains("hidden")`. The guide's row
 * is hidden by a class today, but a row hidden any other way - a media query, an edition fork, a
 * parent - would fool a class check into sending somebody to a page that is not on their screen.
 * Ask the layout, not the markup.
 */
document.getElementById("nav-home")?.addEventListener("click", e => {
	e.preventDefault();
	const first = [...document.querySelectorAll<HTMLElement>(".nav-links [data-section]")].find(link => link.offsetParent != null);
	navigateTo(first == null ? "dashboard" : sectionEntry(first.dataset.section!));
});

/**
 * `/releases/12/link` → `{ page: "releases", subPath: ["12", "link"] }`.
 *
 * ⚠ **It reads the PATH as of 2026-08-10** (owner ask - real path navigation, no hashes). Everything
 * about the shape is unchanged; only where the string comes from moved, which is why every page's
 * `takeSubPath()` contract survived the change untouched.
 */
function parseRoute(): { page: string; subPath: string[] } {
	const [page, ...subPath] = (location.pathname.replace(/^\/+/, "") || "dashboard").split("/");
	// ⚠ Through `canonicalPage`, so `/blogs` still opens Outlets after the 2026-08-20 rename. A page
	// name is an address; bookmarks and support answers carry the old one, and a name the router does
	// not recognize falls through to the dashboard - which reads as the app having lost the page.
	return { page: page === "" ? "dashboard" : canonicalPage(page), subPath };
}

/**
 * An address from before the paths landed, rewritten in place before anything reads the URL.
 *
 * ⚠ **The old URLs are not all ours, which is the whole reason this exists** (BACKLOG tier 2 item
 * 16). `config.baseUrl + "/#settings"` is the Stripe billing portal's `return_url` and the
 * checkout's, both stored on Stripe's side and neither of them ours to change; the trial-ending mail
 * carries one; `#settings/account/plans` is an address round 204 built deliberately and anybody may
 * have bookmarked. A path move that stopped answering those would break links held by a third
 * party - the same rule the beacon's URL has, one surface over.
 *
 * ⚠ **`replaceState`, not push**: the hash form is not a place the reader chose to be, so putting it
 * in history means the Back button returns them to a URL that immediately redirects again.
 *
 * ⚠ It runs BEFORE the boot's first read and translates only a hash naming a real page, so
 * `#main-content` - the skip link's target, which has the exact shape of a route - is left alone.
 */
function adoptLegacyHash(): void {
	const path = pathFromLegacyHash(location.hash);
	if (path == null) return;
	history.replaceState(null, "", path + location.search);
}
adoptLegacyHash();

/**
 * ⚠ **A legacy hash can also arrive while the app is already RUNNING**, and this is the half the boot
 * translation cannot cover. Setting `location.hash` on a live page fires `hashchange`, never
 * `popstate` - so without this, an old `#settings` link opened in a tab that already has the app
 * loaded changes the address bar and nothing else happens, which is indistinguishable from a dead
 * link. Two UI tests found it by driving the app the way that link does.
 */
window.addEventListener("hashchange", () => {
	const path = pathFromLegacyHash(location.hash);
	if (path == null) return;
	history.replaceState(null, "", path + location.search);
	const { page, subPath } = parseRoute();
	if (pageLoaders[page]) navigateTo(page, subPath);
});

window.addEventListener("popstate", () => {
	const { page, subPath } = parseRoute();
	// ⚠ `fromHistory`: the browser has already set the address, and writing it again from here is what
	// breaks Forward onto a dialog's URL - see `navigateTo`.
	if (pageLoaders[page]) navigateTo(page, subPath, true);
});

// ── Setup all event handlers ────────────────────────────
// No setupReleaseEvents()/setupDiscoverEvents()/setupBlogEvents()/setupApprovedEvents()/
// setupSentEvents()/setupCampaignEvents()/setupComposeEvents()/setupArtistEvents()/
// setupOnboardingEvents(): those pages are wholly islands now, with no static chrome to wire
// - and since round 74 the artist edit modal renders from its island too, which retired
// locale.ts's boot-time sweep (the last document-wide querySelectorAll+addEventListener pass)

// ── Global keyboard shortcuts ───────────────────────────
document.addEventListener("keydown", e => {
	// Escape closes modals - unless a <dialog> confirmation is on top (it consumes the Escape natively)
	if (e.key === "Escape" && !document.querySelector("dialog[open]")) {
		document.querySelectorAll(".modal:not(.hidden)").forEach(m => {
			m.classList.add("hidden");
		});
		// The phone's nav drawer is the same gesture: Escape means "get this off my screen"
		if (document.body.classList.contains("nav-open")) setNavOpen(false);
	}
	// Ctrl/Cmd+Enter saves in open modal
	if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
		const openModal = document.querySelector(".modal:not(.hidden)");
		if (openModal) {
			const saveBtn = openModal.querySelector<HTMLButtonElement>(".btn-primary");
			if (saveBtn) {
				e.preventDefault();
				saveBtn.click();
			}
		}
	}
});

// ── Boot: edition- and first-run-aware chrome, then the initial navigation ──
// The initial navigation waits for /api/meta (one localhost round-trip) so a fresh
// install's first open can land on the Get-started guide directly - navigating to the
// dashboard first and redirecting after would flash an empty page. On a failed read
// the app boots exactly as before: selfhost chrome, no guide, hash-or-dashboard -
// never hide (or add) surfaces on an error.
interface BootMeta {
	edition: "saas" | "selfhost";
	onboarding: { needed: boolean; firstRun: boolean };
	/** Has this workspace ever said what zone it is in? (round 161) False exactly once. */
	timeZoneSet?: boolean;
	/** Where ffmpeg.wasm is served from, when this server offers the client render lane (§AJ) */
	renderEngine?: string | null;
	auth: AuthMeta;
}
void (async () => {
	let meta: BootMeta | null = null;
	try {
		meta = await api<BootMeta>("/api/meta");
	} catch {}
	// Signed out on a saas server: show the sign-in screen and boot NOTHING else - every
	// page loader would only collect 401s. Successful sign-in reloads, re-running this
	// boot with a session. Selfhost never enters this branch (auth.enabled is false).
	if (meta?.auth.enabled && !meta.auth.user) {
		showLoginScreen(meta.auth);
		return;
	}
	// Pages read the edition/operator flags from here (frontend/meta.ts); the failed-fetch
	// default inside setAppContext's module is selfhost+operator, matching this fallback
	setAppContext({
		saas: !!meta?.auth.enabled,
		operator: meta?.auth.enabled ? meta.auth.operator : true,
		guideOpen: meta?.onboarding.needed === true,
		renderEngine: typeof meta?.renderEngine === "string" ? meta.renderEngine : null,
		guest: meta?.auth.guest === true,
		example: meta?.auth.example != null,
		google: meta?.auth.google === true,
		emailDelivery: meta?.auth.emailDelivery ?? "console"
	});
	// Which AI capabilities this install can honor (round 117), started here so it overlaps the
	// chrome work below and awaited before the first render - every AI button reads it
	// synchronously to gray itself with a reason, and a button that flickered from live to
	// disabled would be worse than either. Started AFTER the signed-out branch above so it can
	// never fire a doomed request at a login screen.
	const aiStatus = loadAiStatus();
	// The same question for the owner's MAILBOX (2026-08-11): the Sent page's Check Replies and Scan
	// Sent Mail need a reading lane, and without one they were live buttons whose whole behavior was a
	// red toast. Started here, awaited beside the AI one for the same reason - a control that flickers
	// from live to disabled is worse than either state.
	const mailStatus = loadMailStatus();
	// The attention badge (round 160): the count of promises the app is not keeping, following the
	// owner around the app instead of waiting on the dashboard for them to come home. Started here
	// for the same reason as the AI status - after the signed-out branch, so it can never fire a
	// doomed request at a login screen - but NOT awaited: chrome that arrives a moment late is fine,
	// and nothing renders until it has a number.
	initAttentionBadge(onNavigate);
	// The Outreach tabs' counts (§AL): the same "chrome that arrives a moment late is fine" as the badge.
	initTabCounts(onNavigate);
	// Help, reachable from every screen (§T round 3, 2026-08-07).
	//
	// ⚠ **BOTH editions, and that reverses the call made an hour earlier in the same session.** The
	// first version mounted it only in saas, on the argument that a permanent floating button for a
	// link to the issue tracker is chrome on an edition with no support desk. That argument was right
	// while Settings still had a Help tab and wrong the moment the owner asked for the tab to go: the
	// bubble is now the ONLY route to "where do I report this?", and a selfhost owner losing it
	// entirely is a worse outcome than a 48px button they may never press. `<gh-help-chat>` already
	// forks its contents on `supportEnabled()`, so selfhost opens straight onto the issue tracker.
	// **A cost/benefit changes when the alternative does; re-derive it rather than inheriting it.**
	//
	// ⚠ Mounted here rather than placed in `index.html` because it must not exist on the login
	// screen - the signed-out branch above has already returned by this line.
	//
	// ⚠ It is a child of <body>, not of a page section. `position: fixed` inside a shadow root is
	// resolved against the nearest ancestor carrying `transform`/`filter`/`backdrop-filter`, and
	// `.page.active` has bitten a modal for exactly that reason before (see public/style.css).
	// ⚠ Not in the EXAMPLE: a ticket opened from it would be about a workspace nobody owns, answered on
	// the operator's own AI, and the send is refused there anyway.
	if (meta?.auth.example == null)
		void import("./components/helpBubble.ts").then(() => document.body.appendChild(document.createElement("gh-help-bubble")));
	// The workspace's clock (round 161). The browser is the only thing in the system that knows
	// where the person actually is, and every deadline the app acts on is a wall-clock claim: a rung
	// due today, a digest "after 08:00", release-day mail, the posting-time board's bands. Sent ONCE
	// - `timeZoneSet` is false only until somebody (this line, or the settings card) answers - and
	// the endpoint refuses to overwrite an answer, so it can never argue with a choice. Not awaited:
	// nothing on screen depends on it, and a failed send simply asks again next boot.
	if (meta?.timeZoneSet === false) {
		void api("/api/settings/timezone/detected", { method: "POST", body: { zone: Intl.DateTimeFormat().resolvedOptions().timeZone } }).catch(
			() => undefined
		);
	}
	if (meta?.auth.enabled && meta.auth.user) {
		// From here on a 401 means THIS session expired - api() may now reload on it
		armSessionReload();
		const account = document.getElementById("nav-account");
		const email = document.getElementById("nav-account-email");
		// ⚠ Not for a GUEST (§AM): there is no address to show, and "Sign out" is the one control that
		// would throw a guest's work away with nothing to sign back in with. The claim card takes this
		// slot for them.
		if (account && email && meta.auth.guest !== true && meta.auth.user.email != null) {
			email.textContent = meta.auth.user.email;
			account.classList.remove("hidden");
			document.getElementById("nav-account-signout")?.addEventListener("click", () => {
				// To /signin, not a reload: since round 137 a reload at "/" without a session lands on
				// the LANDING PAGE, and someone who just signed out is far more likely to be switching
				// accounts than reading the pitch. It also drops the stale `#page` hash, which would
				// otherwise ride along onto whatever answered.
				void api("/api/auth/logout", { method: "POST" }).finally(() => location.replace(SIGNIN_PATH));
			});
		}
		// §AM: a GUEST gets the claim where a signed-in account shows its email - in the sidebar, and in the
		// phone's top bar, where the sidebar is a drawer nobody sees until they open it (owner: *"present
		// and clearly visible in the UI"*). Appearing after /api/meta is the right direction for chrome
		// that depends on it (src/frontend/CLAUDE.md: a thing that arrives late reads as loading).
		// THE EXAMPLE gets the same two slots (`/example`): what this workspace is, and the way out.
		const example = meta.auth.example;
		if (example != null) {
			const guestStart = meta.auth.guestStart === true;
			const card = document.createElement("gh-example-bar");
			card.variant = "sidebar";
			card.signedIn = example.signedIn;
			card.guestStart = guestStart;
			document.getElementById("nav-claim-slot")?.replaceChildren(card);
			const bar = document.createElement("gh-example-bar");
			bar.variant = "bar";
			bar.signedIn = example.signedIn;
			bar.guestStart = guestStart;
			document.getElementById("topbar-claim-slot")?.replaceChildren(bar);
		}
		if (meta.auth.guest === true) {
			const card = document.createElement("gh-claim-card");
			card.variant = "sidebar";
			document.getElementById("nav-claim-slot")?.replaceChildren(card);
			const bar = document.createElement("gh-claim-card");
			bar.variant = "bar";
			document.getElementById("topbar-claim-slot")?.replaceChildren(bar);
		}
		// A claim comes back with its outcome on the address (routes/auth.ts). Read once, then dropped, so
		// a reload does not say it twice. "exists" is Google's conflict: the dialog opens on it.
		const claimOutcome = new URLSearchParams(location.search).get("claim");
		if (claimOutcome != null) {
			const clean = new URL(location.href);
			clean.searchParams.delete("claim");
			history.replaceState(history.state, "", `${clean.pathname}${clean.search}${clean.hash}`);
			if (claimOutcome === "done") toast(t("claim.doneToast"), 6000, "success");
			else if (claimOutcome === "exists") showClaimDialog("exists", { fromGoogle: true });
		}
	}
	// The SaaS build has no per-tenant Discover surface: outlet discovery is the priciest AI lane in
	// this app, so the OPERATOR runs it once into the catalog every tenant reads (`src/crew/` →
	// Outlets, and `services/outletCatalog.ts` for the argument). The selfhost build keeps everything,
	// deliberately and by the owner's explicit instruction - an owner there brings their own keys and
	// scrapes their own outlets into their own file, and no operator is involved anywhere.
	//
	// ⚠ **This comment said the same thing for forty-seven rounds while it was FALSE**, and that is
	// the note worth keeping. "The SaaS build centralizes outlet discovery server-side" described
	// something nobody had built and nothing had replaced, so a hosted tenant had an Outlets page over
	// a table with no path by which any outlet could ever enter it - an inert product, and a comment
	// telling the next reader to look elsewhere. Round 153's rule, in code rather than in a doc: **a
	// stale claim is worse than a missing one, because it tells the reader to stop.** A comment that
	// justifies REMOVING a feature is the shape to distrust; it is the only kind whose falsehood
	// leaves no symptom, because what it describes is an absence either way.
	// ⚠ Driven by `SAAS_ABSENT_SURFACES` since 2026-08-09, rather than by the page name written here.
	// The support assistant has to know what a hosted tenant does not have (it was recommending this
	// very page to people who cannot see it), and a second hand-written list is one that goes stale
	// without a symptom - so the removal reads the same list the prompt does.
	// ⚠ **BOTH editions act here now, and only one of them removes anything** (owner, 2026-08-10: "in
	// the SaaS, on load, I see a flash of Discover Outlets before it disappears"). The nav is static
	// markup in ONE index.html shipped to both editions, and this line runs after a round-trip - so for
	// as long as `/api/meta` takes, a paying tenant was looking at a surface they do not have, and then
	// watched it vanish. A thing that appears late reads as loading; a thing that disappears reads as
	// the page breaking. So the shell ships these items hidden and SELFHOST is the edition that has to
	// be told to show them. ⚠ Both halves are driven off the same list, which is what keeps a second
	// absent surface from being half-wired - and the failure of the hidden class is silent (the removal
	// still works, the flash just comes back), hence the gate in tests/supportAi.test.ts.
	for (const surface of SAAS_ABSENT_SURFACES) {
		const item = document.querySelector(`[data-page="${surface.page}"]`)?.closest("li");
		if (meta?.edition === "saas") item?.remove();
		// A failed meta read boots as selfhost (see the comment above BootMeta), which means revealing
		// these: the fallback must never be the edition that hides surfaces.
		else item?.classList.remove("hidden");
	}
	if (meta?.onboarding.needed) document.getElementById("nav-onboarding")?.classList.remove("hidden");
	// Both reveals above change where a job opens, so the rows' hrefs are derived after them.
	refreshSectionHrefs();
	await aiStatus;
	await mailStatus;
	const init = parseRoute();
	// Only a ROOT open is redirected - a deep link always wins over the guide. ⚠ It used to test
	// `!location.hash`, and the path equivalent has to be the ROOT rather than "no route": every
	// address is a path now, so there is no such thing as an address with no route in it, and a naive
	// port of that condition would have sent every deep link to the guide.
	const atRoot = location.pathname === "/" || location.pathname === "";
	const known = pageLoaders[init.page] != null;
	// ⚠ **`known` is passed as `fromHistory`, and without it the boot DELETES a dialog's key.** The
	// address already says where we are, so this navigation must not rewrite it - and a navigation
	// rewrite drops the `?d=` param on purpose (a dialog belongs to the page you left). Opening the
	// app ON `/artists?d=edit-12` therefore stripped the key one line before the page could read it,
	// and the dialog never opened. The one case that SHOULD write is the fallback below: an unknown
	// page has to become `/dashboard`, dialog and all.
	if (atRoot && meta?.onboarding.firstRun) navigateTo("onboarding");
	else navigateTo(known ? init.page : "dashboard", known ? init.subPath : [], known);
	// The other seventeen pages, once this one has drawn and the browser is idle - see prefetchPages().
	prefetchPages();
})();
